import { PauseIcon, PlayIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import { type CSSProperties, useState } from 'react';

import styles from './music.module.css';

import clsxm from '@/lib/clsxm';

import { Stage } from '@/components/agents/primitives';

import {
  getMusicPreset,
  getMusicPresetPreview,
  type MusicPreset,
} from '@/constants/musicPresets';

import { formatDuration, useMusicPlayer, useMusicT } from './kit';

/**
 * The hero's player: real songs made with the generator, each with the
 * prompt that made it. Plays the 30-second previews of the page's sounds.
 */
export default function NowPlaying({ sounds }: { sounds: string[] }) {
  const { t } = useMusicT();
  const player = useMusicPlayer();
  const tracks = sounds
    .map((id) => getMusicPreset(id))
    .filter((preset): preset is MusicPreset =>
      Boolean(preset && getMusicPresetPreview(preset.id)),
    )
    .slice(0, 4);
  const [currentId, setCurrentId] = useState(tracks[0]?.id);
  const current = tracks.find((track) => track.id === currentId) ?? tracks[0];
  if (!current) return null;

  const preview = getMusicPresetPreview(current.id);
  if (!preview) return null;
  const playing = player.playingSrc === preview.audio;
  const elapsed = playing ? player.progress * preview.duration : 0;

  const play = (track: MusicPreset) => {
    const trackPreview = getMusicPresetPreview(track.id);
    if (!trackPreview) return;
    setCurrentId(track.id);
    player.toggle(trackPreview.audio);
  };

  return (
    <Stage
      variant='ink'
      halftoneAt='100% 0%'
      halftoneReach={70}
      className='p-5 shadow-[0_40px_90px_-40px_rgba(30,20,90,0.65)] sm:p-7'
    >
      <div className='flex items-center justify-between gap-3'>
        <p className='text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50'>
          {t('Made with this generator')}
        </p>
        <span className='rounded-full bg-white/[0.08] px-2.5 py-1 text-[11px] font-medium text-white/70 ring-1 ring-inset ring-white/10'>
          {t('30-second previews')}
        </span>
      </div>

      <div className='mt-6 flex items-center gap-5 sm:gap-6'>
        <div className='relative me-7 h-28 w-28 flex-none sm:me-10 sm:h-40 sm:w-40'>
          {/* A record slides out from behind the cover and turns while it plays. */}
          <div
            aria-hidden='true'
            data-playing={playing}
            className={clsxm(
              styles.disc,
              'absolute inset-y-2 end-0 aspect-square translate-x-[18%] rounded-full bg-[radial-gradient(circle,#2a2940_0_18%,#0b0a16_19%_22%,#1b1a2b_23%_100%)] shadow-[0_10px_30px_rgba(0,0,0,0.55)] transition-transform duration-500 rtl:-translate-x-[18%]',
              playing && 'translate-x-[30%] rtl:-translate-x-[30%]',
            )}
          >
            <div className='absolute inset-[38%] rounded-full bg-gradient-to-br from-primary-400 to-fuchsia-400' />
          </div>
          <div
            className='relative h-full w-full overflow-hidden rounded-2xl shadow-[0_18px_40px_-12px_rgba(0,0,0,0.7)] ring-1 ring-white/10'
            style={{
              backgroundImage: `linear-gradient(135deg, ${current.gradient[0]}, ${current.gradient[1]})`,
            }}
          >
            <Image
              key={preview.cover}
              src={preview.cover}
              alt={t('Cover art for {{name}}', { name: t(current.title) })}
              fill
              unoptimized
              priority
              sizes='160px'
              className='object-cover'
            />
          </div>
        </div>

        <div className='min-w-0 flex-1'>
          <p className='line-clamp-2 text-lg font-semibold leading-snug text-white sm:text-xl'>
            {t(current.title)}
          </p>
          <p className='mt-1 line-clamp-2 text-sm leading-snug text-white/60'>
            {t(current.description)}
          </p>
          <div className='mt-4 flex items-center gap-3'>
            <button
              type='button'
              onClick={() => play(current)}
              aria-label={`${playing ? t('Pause') : t('Play')}: ${t(current.title)}`}
              aria-pressed={playing}
              className='flex h-12 w-12 flex-none items-center justify-center rounded-full bg-white text-[#10101c] shadow-lg transition-transform duration-150 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 active:scale-95'
            >
              {playing ? (
                <PauseIcon className='h-6 w-6' />
              ) : (
                <PlayIcon className='h-6 w-6 translate-x-px' />
              )}
            </button>
            <div
              aria-hidden='true'
              data-playing={playing}
              className={clsxm(styles.bars, 'flex h-6 items-end gap-[3px]')}
            >
              {[0, 180, 90, 270, 140].map((ms) => (
                <span
                  key={ms}
                  className='block h-full w-[3px] rounded-full bg-primary-300'
                  style={{ '--d': `${ms}ms` } as CSSProperties}
                />
              ))}
            </div>
            <span className='ms-auto text-xs tabular-nums text-white/55'>
              {formatDuration(elapsed)} / {formatDuration(preview.duration)}
            </span>
          </div>
        </div>
      </div>

      <div
        className='mt-5 h-1 overflow-hidden rounded-full bg-white/10'
        role='presentation'
      >
        <div
          className='h-full rounded-full bg-gradient-to-r from-primary-300 to-fuchsia-300 transition-[width] duration-200'
          style={{
            width: `${Math.round((playing ? player.progress : 0) * 100)}%`,
          }}
        />
      </div>

      <div className='mt-5 rounded-2xl bg-white/[0.04] p-4 ring-1 ring-inset ring-white/10'>
        <p className='text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45'>
          {t('The prompt')}
        </p>
        <p
          className='mt-1.5 line-clamp-3 text-sm leading-6 text-white/80'
          dir='ltr'
        >
          {current.style}
        </p>
      </div>

      {tracks.length > 1 && (
        <ul className='mt-4 space-y-1'>
          {tracks
            .filter((track) => track.id !== current.id)
            .map((track) => {
              const trackPreview = getMusicPresetPreview(track.id);
              if (!trackPreview) return null;
              return (
                <li key={track.id}>
                  <button
                    type='button'
                    onClick={() => play(track)}
                    className='group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-start transition-colors hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300'
                  >
                    <span className='relative h-10 w-10 flex-none overflow-hidden rounded-lg ring-1 ring-white/10'>
                      <Image
                        src={trackPreview.cover}
                        alt=''
                        fill
                        unoptimized
                        loading='lazy'
                        sizes='40px'
                        className='object-cover'
                      />
                      <span className='absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100'>
                        <PlayIcon className='h-4 w-4 text-white' />
                      </span>
                    </span>
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-sm font-medium text-white'>
                        {t(track.title)}
                      </span>
                      <span className='block truncate text-xs text-white/50'>
                        {t(track.description)}
                      </span>
                    </span>
                    <span className='text-xs tabular-nums text-white/45'>
                      {formatDuration(trackPreview.duration)}
                    </span>
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </Stage>
  );
}
